import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsPage = ({ theme }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--background)] pt-24 pb-16 px-6" data-testid="terms-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-[var(--foreground)]">
          Terms and Conditions
        </h1>
        <p className="text-[var(--muted)] mb-12">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        {/* Content */}
        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">1. Agreement to Terms</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              By accessing or using Hotpeek's services, you agree to be bound by these Terms and Conditions. 
              If you disagree with any part of these terms, you may not access our services. These Terms and 
              Conditions apply to all visitors, users, and others who access or use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">2. Services Description</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Hotpeek provides creator management services including but not limited to account management, 
              content strategy, marketing promotion, chat support, and revenue optimization. The specific 
              services provided will be outlined in individual agreements with each creator.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">3. Eligibility Requirements</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              To use our services, you must be at least 18 years of age and legally able to enter into 
              binding contracts. By using our services, you represent and warrant that you meet all 
              eligibility requirements. We reserve the right to verify your eligibility at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">4. Creator Responsibilities</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              As a creator working with Hotpeek, you are responsible for ensuring all content you create 
              and distribute complies with applicable laws and platform guidelines. You retain ownership 
              of your content while granting Hotpeek necessary licenses to provide our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">5. Payment Terms</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Revenue sharing percentages and payment schedules will be outlined in your individual 
              creator agreement. Payments are processed on a weekly basis. Hotpeek reserves the right 
              to withhold payments in cases of suspected fraud or terms violations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">6. Confidentiality</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Both parties agree to maintain confidentiality regarding proprietary information, business 
              strategies, and financial details shared during the course of the partnership. This 
              obligation survives termination of the agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">7. Termination</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Either party may terminate the agreement with written notice as specified in the individual 
              creator contract. Upon termination, all outstanding payments will be settled according to 
              the agreed payment schedule.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">8. Limitation of Liability</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              Hotpeek shall not be liable for any indirect, incidental, special, consequential, or 
              punitive damages resulting from your use of our services. Our total liability shall not 
              exceed the amount paid to you in the previous twelve months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">9. Changes to Terms</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify you of any changes 
              by posting the new Terms and Conditions on this page and updating the "Last updated" date. 
              Your continued use of our services constitutes acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-[var(--foreground)]">10. Contact Us</h2>
            <p className="text-[var(--muted)] leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us at 
              legal@hotpeek.com or through our application form.
            </p>
          </section>
        </div>

        {/* Footer Note */}
        <div className={`mt-16 p-6 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-[var(--muted)] text-sm">
            <strong className="text-[var(--foreground)]">Note:</strong> This is placeholder content. 
            Please consult with a legal professional to create terms and conditions specific to your 
            business operations and jurisdiction.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
